import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';

function toneColors(colors, tone) {
  if (tone === 'pass') {
    return {
      color: colors.success,
      background: `${colors.success}14`,
      border: `${colors.success}44`,
      dot: colors.success,
    };
  }
  if (tone === 'warn') {
    const warning = colors.warning || colors.orange || '#ED8936';
    return {
      color: warning,
      background: `${warning}18`,
      border: `${warning}55`,
      dot: warning,
    };
  }
  return {
    color: colors.textMuted,
    background: `${colors.border}80`,
    border: colors.border,
    dot: colors.textMuted,
  };
}

function formatLimit(n) {
  if (!Number.isFinite(n)) return '—';
  const abs = Math.abs(n);
  const digits = abs >= 1 ? 2 : 4;
  return n.toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits });
}

/**
 * When a working limit is live, entry-setup chips describe a *new* signal.
 * Surface pending-fill status first so they aren't read as "this bracket is invalid".
 * When an entry was skipped for size, surface that instead of a zombie bracket.
 */
export function criteriaWithWorkingOrder(items = [], workingOrder = null, skippedOrder = null) {
  const base = items || [];
  if (workingOrder) {
    const side = workingOrder.side === 'short' ? 'Short' : 'Long';
    const limit = formatLimit(Number(workingOrder.entryPrice));
    return [
      {
        id: 'pending-limit',
        label: `${side} limit working @ ${limit}`,
        pass: true,
        tone: 'warn',
      },
      {
        id: 'pending-hint',
        label: 'Waiting for fill (setup chips = new signal only)',
        pass: false,
        tone: 'muted',
      },
      ...base,
    ];
  }
  if (skippedOrder?.rejectReason === 'insufficient_qty') {
    return [
      {
        id: 'skipped-size',
        label: 'Entry skipped: risk too small for 1 contract at stop',
        pass: false,
        tone: 'warn',
      },
      {
        id: 'skipped-hint',
        label: 'Raise risk % / max risk, or use Fixed qty',
        pass: false,
        tone: 'muted',
      },
      ...base,
    ];
  }
  return base;
}

export default function StrategyCriteria({ items = [] }) {
  const { colors } = useTheme();
  if (!items.length) {
    return (
      <div style={{ fontSize: 12, color: colors.textMuted }}>Run to see criteria</div>
    );
  }
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
      {items.map((item) => {
        const tone = item.tone || (item.pass ? 'pass' : 'muted');
        const palette = toneColors(colors, tone);
        return (
          <span
            key={item.id || item.label}
            data-tone={tone}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '4px 8px',
              borderRadius: 999,
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: '0.02em',
              color: palette.color,
              background: palette.background,
              border: `1px solid ${palette.border}`,
            }}
          >
            <span
              aria-hidden
              style={{
                width: 6,
                height: 6,
                borderRadius: 99,
                background: palette.dot,
              }}
            />
            {item.label}
          </span>
        );
      })}
    </div>
  );
}
